import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { clioWebhooksApi } from '../api';
import type { CreateWebhookRequest } from '../types';

/**
 * Query key factory for Clio webhooks
 */
export const clioWebhookQueryKeys = {
  all: ['clio-webhooks'] as const,
  inspection: ['clio-inspection'] as const,
};

/**
 * Hook to fetch Clio webhook subscriptions
 */
export function useClioWebhooks() {
  return useQuery({
    queryKey: clioWebhookQueryKeys.all,
    queryFn: () => clioWebhooksApi.getWebhooks(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

/**
 * Hook to fetch Clio webhook inspection
 */
export function useClioWebhookInspection(enabled: boolean = false) {
  return useQuery({
    queryKey: clioWebhookQueryKeys.inspection,
    queryFn: () => clioWebhooksApi.getClioWebhookInspection(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create Clio webhook with auto-enable
 */
export function useCreateClioWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateWebhookRequest) => clioWebhooksApi.createWithAutoEnable(request),
    onSuccess: result => {
      if (result.success) {
        showToast.success(
          `Clio webhook created with auto-processing ${result.autoProcessingEnabled ? 'enabled' : 'disabled'}`
        );
        queryClient.invalidateQueries({ queryKey: clioWebhookQueryKeys.all });
      } else {
        showToast.error(`Failed to create Clio webhook: ${result.message}`);
      }
    },
    onError: (error: Error) => {
      showToast.error(`Failed to create Clio webhook: ${error.message}`);
    },
  });
}

/**
 * Hook to delete Clio webhook completely
 */
export function useDeleteClioWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modelName: string) => clioWebhooksApi.deleteComplete(modelName),
    onSuccess: (result, modelName) => {
      if (result.success) {
        showToast.success(`Clio webhook for ${modelName} and all resources deleted successfully`);
        queryClient.invalidateQueries({ queryKey: clioWebhookQueryKeys.all });
      } else {
        showToast.error(`Failed to delete Clio webhook: ${result.message}`);
      }
    },
    onError: (error: Error) => {
      showToast.error(`Failed to delete Clio webhook: ${error.message}`);
    },
  });
}

/**
 * Hook to recreate Clio webhook from config
 */
export function useRecreateClioWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelName, events }: { modelName: string; events?: string[] }) =>
      clioWebhooksApi.recreateFromConfig(modelName, events),
    onSuccess: (result, { modelName }) => {
      if (result.success) {
        showToast.success(
          `Clio webhook for ${modelName} recreated with auto-processing ${result.autoProcessingEnabled ? 'enabled' : 'disabled'}`
        );
        queryClient.invalidateQueries({ queryKey: clioWebhookQueryKeys.all });
      } else {
        showToast.error(`Failed to recreate Clio webhook: ${result.message}`);
      }
    },
    onError: (error: Error) => {
      showToast.error(`Failed to recreate Clio webhook: ${error.message}`);
    },
  });
}

/**
 * Hook to recreate all Clio webhooks
 */
export function useRecreateAllClioWebhooks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clioWebhooksApi.recreateAll(),
    onSuccess: result => {
      if (result.success) {
        const successCount = result.results.filter(r => r.success).length;
        const totalCount = result.results.length;
        showToast.success(`Recreated ${successCount} of ${totalCount} Clio webhooks successfully`);

        // Show individual failures
        result.results
          .filter(r => !r.success)
          .forEach(r => {
            showToast.error(`Failed to recreate ${r.modelName}: ${r.error}`);
          });

        queryClient.invalidateQueries({ queryKey: clioWebhookQueryKeys.all });
      } else {
        showToast.error('Failed to recreate Clio webhooks');
      }
    },
    onError: (error: Error) => {
      showToast.error(`Failed to recreate all Clio webhooks: ${error.message}`);
    },
  });
}

/**
 * Hook to delete webhook from Clio only
 */
export function useDeleteFromClio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (webhookId: string) => clioWebhooksApi.deleteFromClio(webhookId),
    onSuccess: (result, webhookId) => {
      if (result.success) {
        showToast.success(`Webhook ${webhookId} deleted from Clio successfully`);
        queryClient.invalidateQueries({ queryKey: clioWebhookQueryKeys.inspection });
      } else {
        showToast.error(`Failed to delete webhook from Clio: ${result.message}`);
      }
    },
    onError: (error: Error) => {
      showToast.error(`Failed to delete webhook from Clio: ${error.message}`);
    },
  });
}

